import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { paymentsApi } from '../api';
import { useAuth } from '../context/AuthContext';

function priceLabel(cents) {
  if (!cents) return { amount: 'Darmowy', suffix: '' };
  return { amount: '$' + Math.round(cents / 100), suffix: '/mc' };
}

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    paymentsApi.listPlans()
      .then(r => setPlans(r.data?.items || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const choose = async (plan) => {
    if (!user) { navigate('/register'); return; }
    if (!plan.price_cents) { navigate('/dashboard'); return; }
    setBusy(plan.id);
    try {
      const r = await paymentsApi.checkout(plan.id);
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Nie udało się rozpocząć płatności');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-16">
        <div className="py-16 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Prosty, przejrzysty cennik
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Zacznij za darmo, skaluj gdy rośniesz. Bez ukrytych opłat, anuluj kiedy chcesz.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : plans.length === 0 ? (
            <p className="text-center text-slate-500 py-16">Brak dostępnych planów.</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map(plan => {
                const p = priceLabel(plan.price_cents);
                const popular = plan.id === 'plan_pro';
                return (
                  <div
                    key={plan.id}
                    className={`rounded-2xl border p-6 flex flex-col ${popular ? 'border-indigo-500 shadow-lg ring-1 ring-indigo-200' : 'border-slate-200'}`}
                  >
                    {popular && (
                      <span className="self-start text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 mb-2">
                        Najpopularniejszy
                      </span>
                    )}
                    <h3 className="text-lg font-semibold text-slate-900">{plan.name}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{plan.description}</p>
                    <div className="mt-4 mb-4">
                      <span className="text-3xl font-bold text-slate-900">{p.amount}</span>
                      <span className="text-slate-500">{p.suffix}</span>
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {(plan.features || []).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => choose(plan)}
                      disabled={busy === plan.id}
                      className={`w-full py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 ${
                        popular
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {busy === plan.id
                        ? 'Przekierowanie...'
                        : (!plan.price_cents ? 'Zacznij za darmo' : 'Wybierz plan')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
